<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import {
  compareNativeVersions,
  nativeReleaseCatalog,
  nativeDownloadUrl,
  nativeRepository,
} from '@atum-remote/config/native-releases';
import { detectNativeDownload } from '../lib/native-platform.js';
import AppIcon from './AppIcon.vue';
import OsIcon from './OsIcon.vue';

/**
 * Pick a computer, get its installer. The platform is a pair of large OS tiles,
 * preselected from what the browser reports; everything else (older versions,
 * release notes, the hash) sits behind one disclosure.
 */
type Platform = 'windows' | 'macos';
const props = defineProps<{ openExternal: ((url: string) => Promise<void>) | undefined }>();
const emit = defineEmits<{ close: [] }>();
const dialog = ref<HTMLDialogElement>();
const platform = ref<Platform | ''>('');
const detected = ref<Platform | ''>('');
const message = ref('Choose the computer you want to install on.');
const selectedVersion = ref('');
const error = ref('');
const opening = ref(false);
let revision = 0;
const releases = [...nativeReleaseCatalog.releases].sort((a, b) =>
  compareNativeVersions(b.version, a.version),
);
const options: readonly {
  id: Platform;
  name: string;
  detail: string;
  requirement: string;
}[] = [
  {
    id: 'windows',
    name: 'Windows',
    detail: 'Intel / AMD 64-bit',
    requirement: 'Windows 10 or 11 · x64 only',
  },
  {
    id: 'macos',
    name: 'macOS',
    detail: 'Universal · Apple silicon & Intel',
    requirement: 'macOS 14 or newer',
  },
];
const target = computed(() =>
  platform.value === 'macos'
    ? 'darwin-universal'
    : platform.value === 'windows'
      ? 'windows-x86_64'
      : undefined,
);
const release = computed(() => releases.find((item) => item.version === selectedVersion.value));
const artifact = computed(() =>
  release.value?.artifacts.find((item) => item.target === target.value),
);
const latestCompatibleRelease = computed(() =>
  target.value
    ? releases.find((item) => item.artifacts.some((file) => file.target === target.value))
    : releases[0],
);
const url = computed(() =>
  release.value && artifact.value ? nativeDownloadUrl(release.value, artifact.value) : undefined,
);
const size = computed(() =>
  artifact.value ? `${(artifact.value.size / 1024 / 1024).toFixed(1)} MB` : '',
);
const published = computed(() =>
  release.value
    ? new Date(release.value.publishedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '',
);
const availability = computed(() =>
  releases.length === 0
    ? 'The first signed ATUM Remote release is not available yet.'
    : !platform.value
      ? 'Choose Windows or macOS to see its installer.'
      : !artifact.value
        ? 'This version has no installer for that platform. Pick another version below.'
        : '',
);
function selectLatestCompatibleVersion() {
  selectedVersion.value = latestCompatibleRelease.value?.version ?? releases[0]?.version ?? '';
}
async function open() {
  const current = ++revision;
  error.value = '';
  platform.value = '';
  detected.value = '';
  selectedVersion.value = releases[0]?.version ?? '';
  message.value = 'Choose the computer you want to install on.';
  dialog.value?.showModal();
  const recommendation = await detectNativeDownload();
  if (revision !== current) return;
  platform.value = recommendation.platform;
  detected.value = recommendation.platform;
  selectLatestCompatibleVersion();
  message.value = recommendation.message;
}
function choose(next: Platform) {
  revision++;
  platform.value = next;
  selectLatestCompatibleVersion();
  message.value =
    next === detected.value
      ? 'Recommended for this computer.'
      : `Download for another ${next === 'macos' ? 'Mac' : 'Windows PC'}.`;
  error.value = '';
}
function close() {
  revision++;
  dialog.value?.close();
}
async function download(event: MouseEvent) {
  if (!props.openExternal || !url.value) return;
  event.preventDefault();
  if (opening.value) return;
  opening.value = true;
  error.value = '';
  try {
    await props.openExternal(url.value);
  } catch {
    error.value = 'The installer link could not be opened. Try again.';
  } finally {
    opening.value = false;
  }
}
onUnmounted(() => {
  revision++;
});
defineExpose({ open });
</script>

<template>
  <dialog
    ref="dialog"
    class="dialog download-dialog"
    aria-labelledby="download-title"
    aria-describedby="download-description"
    @close="
      revision++;
      emit('close');
    "
  >
    <div class="dialog-body">
      <header class="dialog-head">
        <div class="dialog-title">
          <span class="section-icon"><AppIcon name="download" /></span>
          <div>
            <p class="eyebrow">Installed app</p>
            <h2 id="download-title">Download ATUM Remote</h2>
          </div>
        </div>
        <button
          type="button"
          class="icon-button quiet"
          aria-label="Close download dialog"
          @click="close"
        >
          <AppIcon name="close" />
        </button>
      </header>
      <p id="download-description" class="download-lede">
        One app connects to your devices and hosts this computer. Choose each role during setup.
      </p>

      <div class="os-choice" role="radiogroup" aria-label="Platform">
        <button
          v-for="option in options"
          :key="option.id"
          type="button"
          role="radio"
          class="os-option"
          :aria-checked="platform === option.id ? 'true' : 'false'"
          @click="choose(option.id)"
        >
          <span class="os-option-logo"><OsIcon :platform="option.id" /></span>
          <span class="os-option-text">
            <strong>{{ option.name }}</strong>
            <span>{{ option.detail }}</span>
            <span class="os-option-requirement">{{ option.requirement }}</span>
          </span>
          <span v-if="detected === option.id" class="tag os-option-tag">This computer</span>
          <AppIcon name="check" class="os-option-check" />
        </button>
      </div>
      <p class="download-recommendation" role="status">{{ message }}</p>

      <div v-if="release && artifact" class="download-details">
        <div class="download-file">
          <span class="download-file-icon"><AppIcon name="file" /></span>
          <div class="download-file-text">
            <strong>{{ artifact.filename }}</strong>
            <span>Version {{ release.version }} · {{ size }} · released {{ published }}</span>
          </div>
        </div>
        <p class="download-signing">
          <AppIcon name="shield" />
          <span>{{
            artifact.signing === 'updater-signature'
              ? 'Windows installer with cryptographically verified in-app updates.'
              : 'Signed and notarized for macOS.'
          }}</span>
        </p>
        <details class="disclosure">
          <summary>Other versions, release notes &amp; verification</summary>
          <label class="field">
            <span class="field-label">Version</span>
            <select v-model="selectedVersion" aria-label="Version" @change="revision++">
              <option v-for="item in releases" :key="item.version" :value="item.version">
                {{ item.version
                }}{{ item.version === latestCompatibleRelease?.version ? ' · Latest' : '' }}
              </option>
            </select>
          </label>
          <p class="download-notes">{{ release.notes }}</p>
          <span class="field-label">SHA-256</span>
          <code class="download-hash">{{ artifact.sha256 }}</code>
          <p class="field-help">Compare this hash with the downloaded file to verify it.</p>
          <a
            :href="`${nativeRepository}/commit/${release.sourceCommit}`"
            target="_blank"
            rel="noopener noreferrer"
            >Source commit {{ release.sourceCommit.slice(0, 12) }}<AppIcon name="external"
          /></a>
        </details>
      </div>
      <template v-else-if="availability">
        <div class="banner" role="status">
          <AppIcon name="info" /><span>{{ availability }}</span>
        </div>
        <details v-if="platform && releases.length > 1" class="disclosure">
          <summary>Other versions</summary>
          <label class="field">
            <span class="field-label">Version</span>
            <select v-model="selectedVersion" aria-label="Version" @change="revision++">
              <option v-for="item in releases" :key="item.version" :value="item.version">
                {{ item.version }}
              </option>
            </select>
          </label>
        </details>
      </template>
      <div v-if="error" class="banner error" role="alert">
        <AppIcon name="warning" /><span>{{ error }}</span>
      </div>

      <footer class="dialog-actions">
        <button type="button" @click="close">Close</button>
        <a
          v-if="url"
          :href="url"
          class="button-link download-primary"
          target="_blank"
          rel="noopener noreferrer"
          :aria-disabled="opening"
          @click="download"
          ><AppIcon name="download" />Download for {{ platform === 'macos' ? 'Mac' : 'Windows' }}</a
        >
        <button v-else type="button" class="primary" disabled>
          <AppIcon name="download" />Download installer
        </button>
      </footer>
    </div>
  </dialog>
</template>
